import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col
} from 'reactstrap';
// import { counterAPI } from '../../api_helper';

const Counter = () => {
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Fallback data in case API fails
  const fallbackCounters = [
    { id: 1, current: 0, target: 3000, label: 'Community Members', sequence: 1 },
    { id: 2, current: 0, target: 350, label: 'Events', sequence: 2 },
    { id: 3, current: 0, target: 35, label: 'Cities', sequence: 3 }
  ];

  // Fetch counter data from API
  useEffect(() => {
    const fetchCounterData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/public/counts');
        
        if (response.data.isOk) {
          // Sort by sequence field and map to counter format
          const dynamicCounters = response.data.data
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .map((item, index) => ({
              id: item._id || index + 1,
              current: 0, // Start animation from 0
              target: item.count || 0,
              title: item.title || `Counter ${index + 1}`,
              sequence: item.sequence || index + 1,
              _id: item._id
            }));
          
          setCounters(dynamicCounters);
        } else {
          console.warn('No counter data found, using fallback');
          setCounters(fallbackCounters);
        }
      } catch (error) {
        console.error('Error fetching counter data:', error);
        setError(error.message);
        // Use fallback data on error
        setCounters(fallbackCounters);
      } finally {
        setLoading(false);
      }
    };

    fetchCounterData();
  }, []);

  // Intersection Observer to trigger animation when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible && counters.length > 0) {
          setIsVisible(true);
          startCountingAnimation();
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible, counters]);

  // Counter animation function
  const startCountingAnimation = () => {
    if (counters.length === 0) return;
    
    counters.forEach((counter) => {
      const duration = 1000; // 2 seconds
      const steps = 60; // 60 steps for smooth animation
      const increment = counter.target / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.ceil(increment * currentStep), counter.target);
        
        setCounters(prevCounters => 
          prevCounters.map(c => 
            c._id === counter._id ? { ...c, current: newValue } : c
          )
        );

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, duration / steps);
    });
  };

  if (loading) {
    return (
      <section className="counter-section text-center" ref={sectionRef}>
        <Container>
          <Row className="g-4 justify-content-center">
            <Col xs={12}>
              <div className="d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary me-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Loading counters...</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  return (
    <section className="counter-section text-center" ref={sectionRef}>
      <Container>
        {error && (
          <Row className="mb-3">
            <Col xs={12}>
              <div className="alert alert-warning">
                <small>
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  Unable to load dynamic counters. Displaying default values.
                </small>
              </div>
            </Col>
          </Row>
        )}
        <Row className="g-4">
          {counters.map((counter) => (
            <Col xs={12} md={4} key={counter._id}>
              <h2 className="counter counter-variable">
                {(counter.current || 0).toLocaleString() + "+"}
              </h2>
              <p className="mb-0 counter-text">{counter.title}</p>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default Counter;
